import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import ConversationsSidebarContainer from "@/components/chat/ConversationsSidebarContainer";
import ChatAreaChild from "@/components/chat/ChatAreaChild";
import { useGetChatRoomsQuery } from "@/api/chatApi/chat";
import { ChatContext, ChatContextInterface } from "@/components/MSRContext";
import Global from "@/globals/globals";
import type { ChatRoom } from "@/types/chat.types";
import generateRandomString from "../MSRP/utilities/generators";
import { createEmptyMultiPart, MessagePart } from "../utils/multiPartBuilder";
import { MyRequest } from "../models/MyRequest";
import { Message } from "../models/Message";
import { FileMessage } from "../models/FileMessage";
import { isAgentUserType } from "@/utils/userType";

const ROOMS_PAGE_SIZE = 20;

/**
 * ChatPage - New prop-based chat interface
 *
 * This page uses the parent-child architecture:
 * - ConversationsSidebarContainer manages room selection
 * - ChatAreaChild displays messages for selected room
 * - NO URL navigation - everything is prop-based
 */

const ChatPage: React.FC = () => {
  // Get MSRP wrapper, context functions, and rooms from WebSocket
  const { wrapper, handleaddServerRequest, rooms: contextRooms, controller, handlemarkAsRead } = useContext(ChatContext) as ChatContextInterface;

  // Track selected room for MSRP operations
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allRooms, setAllRooms] = useState<ChatRoom[]>([]);
  const [hasMoreRooms, setHasMoreRooms] = useState(true);
  const isLoadingMoreRef = useRef(false);
  
  const currentPageRef = useRef(currentPage);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  const toUnixSeconds = (value: unknown): number | undefined => {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? undefined : Math.floor(value.getTime() / 1000);
    }

    if (typeof value === "number") {
      return value > 9_999_999_999 ? Math.floor(value / 1000) : value;
    }

    if (typeof value === "string" && value.trim()) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : Math.floor(parsed.getTime() / 1000);
    }

    return undefined;
  };

  const getMessagePreview = (message: any): string => {
    if (!message) return "";
    if (typeof message.text === "string" && message.text.trim()) return message.text;
    if (typeof message.message === "string" && message.message.trim()) return message.message;
    if (typeof message.fileName === "string" && message.fileName.trim()) return `📎 ${message.fileName}`;
    return "";
  };

  const withCompanyDomain = (name: string): string => {
    const normalized = String(name || "").trim();
    if (!normalized) return "";
    return normalized.includes("@") ? normalized : `${normalized}${Global.companyDomain || ""}`;
  };

  const getReceiverUsername = (room: ChatRoom): string => {
    if (room.session_type !== "agent-to-agent") return "";

    const receiver = room.participants?.find((participant) => participant.is_current_user === false);
    const details = receiver?.details as any;

    if (receiver?.details && isAgentUserType(receiver.user_type)) {
      const rawName =
        details?.agent_username ||
        details?.username ||
        details?.admin_username ||
        details?.display_name ||
        "";
      return withCompanyDomain(rawName);
    }

    const sessionParts = String(room.session_key || "")
      .replace(/^a2a:/, "")
      .split("+")
      .map((part) => part.trim())
      .filter(Boolean);
    const fallbackPeer = sessionParts.find((part) => part !== withCompanyDomain(Global.username));
    return withCompanyDomain(fallbackPeer || "");
  };

  const getPeerUsernameFromRoom = (room: ChatRoom): string => {
    const rawRoomName = String(room.session_key || "")
      .replace(/^a2a:/, "")
      .replace(/^conf:/, "")
      .replace(/^grpcht:/, "");

    const sessionParts = rawRoomName
      .split("+")
      .map((part) => part.trim())
      .filter(Boolean);

    const currentUser = withCompanyDomain(Global.username);
    const peerName = sessionParts.find((part) => part !== currentUser);
    if (peerName) {
      return withCompanyDomain(peerName);
    }

    const fallbackParticipant = room.participants?.find((participant) => participant.is_current_user === false);
    const fallbackDetails = fallbackParticipant?.details as any;
    const fallbackName =
      fallbackDetails?.agent_username ||
      fallbackDetails?.username ||
      fallbackDetails?.admin_username ||
      fallbackDetails?.client_username ||
      fallbackDetails?.display_name ||
      "";

    return withCompanyDomain(fallbackName);
  };

  const mergeRoomsWithControllerState = useCallback((rooms: ChatRoom[]) => {
    const controllerRoomsById = new Map(controller.chatRooms.map((room) => [room.chatRoomId, room]));

    return rooms.map((apiRoom) => {
      const controllerRoom = controllerRoomsById.get(apiRoom.session_key);
      if (!controllerRoom) {
        return apiRoom;
      }

      const lastControllerMessage = controllerRoom.messages[controllerRoom.messages.length - 1];
      const preview = getMessagePreview(lastControllerMessage);
      const latestTime =
        toUnixSeconds(lastControllerMessage?.arrivalTime) ??
        toUnixSeconds(lastControllerMessage?.deliveredTime) ??
        toUnixSeconds(lastControllerMessage?.sentTime) ??
        apiRoom.last_message_at;

      return {
        ...apiRoom,
        unread_count: controllerRoom.unreadMessageCount,
        last_message: preview
          ? {
              ...(apiRoom.last_message || {}),
              text: preview,
            }
          : apiRoom.last_message,
        last_message_at: latestTime,
      };
    });
  }, [controller]);

  // Fetch rooms via RTK Query (include=messages for instant message display)
  const { data, isLoading, isFetching, refetch } = useGetChatRoomsQuery({
    personal: true,
    "chat-type": "any",
    "session-status": "both",
    include: "messages",
    message_limit: 25,
    page: currentPage,
    limit: ROOMS_PAGE_SIZE,
  });

  // Update allRooms when data changes
  useEffect(() => {
    if (data?.chatRooms) {
      const newRooms = data.chatRooms as ChatRoom[];
      const mergedRooms = mergeRoomsWithControllerState(newRooms);

      if (currentPage === 1) {
        // First page - replace all rooms
        setAllRooms(mergedRooms);
      } else {
        // Subsequent pages - append new rooms (avoid duplicates)
        setAllRooms(prev => {
          const existingKeys = new Set(prev.map(r => r.session_key));
          const uniqueNewRooms = mergedRooms.filter(r => !existingKeys.has(r.session_key));
          return [...prev, ...uniqueNewRooms];
        });
      }

      // Check if there are more rooms (pagination info is at root level)
      const totalPages = data.totalPages || 1;
      const hasMore = currentPage < totalPages;
      setHasMoreRooms(hasMore);
      isLoadingMoreRef.current = false;

      console.log(`📄 [ROOMS] Page ${currentPage}/${totalPages}, loaded ${newRooms.length} rooms, total: ${currentPage === 1 ? newRooms.length : allRooms.length + newRooms.length}, hasMore: ${hasMore}`);
    }
  }, [data, currentPage, controller, mergeRoomsWithControllerState, selectedRoom?.session_key]);

  // Load more rooms
  const handleLoadMoreRooms = useCallback(() => {
    if (!hasMoreRooms || isFetching || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    console.log(`📄 [ROOMS] Loading page ${currentPage + 1}...`);
    setCurrentPage(prev => prev + 1);
  }, [hasMoreRooms, isFetching, currentPage]);

  const syncRoomsFromState = useCallback(() => {
    setAllRooms((prev) => {
      const baseRooms = prev.length > 0 ? prev : (data?.chatRooms || []);
      return mergeRoomsWithControllerState(baseRooms);
    });

    const apiKeys = new Set((data?.chatRooms as any[] ?? []).map((r) => r.session_key));
    const hasNewRoom = (contextRooms as any[]).some((r) => r.chatRoomId && !apiKeys.has(r.chatRoomId));
    if (hasNewRoom) {
      if (currentPageRef.current !== 1) {
        setCurrentPage(1);
      } else {
        refetch();
      }
    }
  }, [contextRooms, data?.chatRooms, mergeRoomsWithControllerState, refetch]);

  useEffect(() => {
    if (!contextRooms || contextRooms.length === 0) return;
    syncRoomsFromState();
  }, [contextRooms, syncRoomsFromState]);

  // Delete message using MSRP protocol
  const handleDeleteMessage = (messageId: string, _agentUsername?: string) => {
    if (!selectedRoom) return;

    const id = generateRandomString(16);
    const toName = selectedRoom.session_type_id === 3 || selectedRoom.session_type_id === 2
      ? selectedRoom.session_key
      : selectedRoom.session_type === "c2a"
        ? withCompanyDomain(selectedRoom?.client?.client_username || "")
        : selectedRoom.session_type === "agent-to-agent"
          ? getReceiverUsername(selectedRoom)
            : withCompanyDomain(selectedRoom?.agent?.agent_username || "");

    const payload = {
      id,
      type: "request",
      job: "delete-message",
      parameters: {
        "deleted-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "delete-time": Math.floor(Date.now() / 1000),
      },
    };

    wrapper?.sendMsg(JSON.stringify(payload, null, 4), "application/rechat.cntl+json");

    handleaddServerRequest?.(
      new MyRequest(
        id,
        Global.username + Global.companyDomain,
        toName,
        "delete-message",
        "pending",
        "This message has been deleted",
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  // Edit message using MSRP protocol
  const handleEditMessage = (messageId: string, newText: string) => {
    if (!selectedRoom) return;

    const toName =
      selectedRoom.session_type === "c2a"
        ? withCompanyDomain(selectedRoom?.client?.client_username || "") || getPeerUsernameFromRoom(selectedRoom)
        : selectedRoom.session_type === "agent-to-agent"
          ? getReceiverUsername(selectedRoom) || getPeerUsernameFromRoom(selectedRoom)
          : selectedRoom.session_type === "group-chat" || selectedRoom.session_type === "conference"
            ? selectedRoom.session_key || getPeerUsernameFromRoom(selectedRoom)
            : withCompanyDomain(selectedRoom?.agent?.agent_username || "") || getPeerUsernameFromRoom(selectedRoom);

    const multipartMessage = createEmptyMultiPart();
    const reqID = generateRandomString(16);

    const messageJson = {
      id: reqID,
      type: "request",
      job: "edit-message",
      parameters: {
        "current-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "chat-type": selectedRoom.session_type,
      },
    };

    const messageJsonString = JSON.stringify(messageJson, null, 4);

    const part1 = new MessagePart({
      name: "part1",
      contentType: "application/rechat.cntl+json\r\n",
      filename: "file1.txt",
      value: messageJsonString,
      contentDisposition: "attachment",
    });

    const part2 = new MessagePart({
      name: "part2",
      contentType: "text/plain\r\n",
      filename: "file2.txt",
      value: newText,
      contentDisposition: "attachment",
    });

    multipartMessage.addPart(part1);
    multipartMessage.addPart(part2);

    const Body = multipartMessage.toString();
    const Boundary = multipartMessage.boundary;
    const Type = 'multipart/related;boundary="' + Boundary + '"';

    wrapper?.sendMsg(Body, Type);

    setAllRooms((prevRooms) =>
      prevRooms.map((candidate) =>
        candidate.session_key === selectedRoom.session_key
          ? { ...candidate, last_message: { ...(candidate.last_message || {}), text: newText } }
          : candidate
      )
    );

    handleaddServerRequest?.(
      new MyRequest(
        reqID,
        Global.username + Global.companyDomain,
        toName,
        "edit-message",
        "pending",
        newText,
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  const handleSendMessage = (text: string, _sender: string, _isOwner: boolean, _id: string) => {
    if (!selectedRoom) return;
    const now = Math.floor(Date.now() / 1000);
    const controllerRoom = controller.chatRooms.find((room) => room.chatRoomId === selectedRoom.session_key);
    if (controllerRoom) {
      controller.addMessage(
        new Message(
          text,
          _id,
          true,
          selectedRoom.session_key,
          Global.username + Global.companyDomain,
          null,
          new Date(),
          new Date(),
          [],
          null,
          null,
          new Date()
        ),
        selectedRoom.session_key
      );
      (controllerRoom as any).last_message_at = now;
      controller.saveChatRooms();
    }
    setAllRooms(prev => prev.map(room =>
      room.session_key === selectedRoom.session_key
        ? { ...room, last_message: { ...(room.last_message || {}), text }, last_message_at: now }
        : room
    ));
  };

  const handleSendFileMessage = (fileMessage: any) => {
    if (!selectedRoom) return;
    const preview = fileMessage.fileName ? `📎 ${fileMessage.fileName}` : "File";
    const now = Math.floor(Date.now() / 1000);
    const controllerRoom = controller.chatRooms.find((room) => room.chatRoomId === selectedRoom.session_key);
    if (controllerRoom) {
      controller.addMessage(
        new FileMessage(
          fileMessage.text || preview,
          fileMessage.fileName || "",
          fileMessage.filePath || "",
          fileMessage.fileType || "",
          fileMessage.messageId,
          true,
          selectedRoom.session_key,
          Global.username + Global.companyDomain,
          fileMessage.repliedTo || null,
          new Date(),
          new Date(),
          new Date(),
          [],
          null,
          null
        ),
        selectedRoom.session_key
      );
      (controllerRoom as any).last_message_at = now;
      controller.saveChatRooms();
    }
    setAllRooms(prev => prev.map(room =>
      room.session_key === selectedRoom.session_key
        ? { ...room, last_message: { ...(room.last_message || {}), text: preview }, last_message_at: now }
        : room
    ));
  };

  const handleRoomSelect = (room: ChatRoom) => {
    console.log("✅ Room selected via PROPS (no URL change):", room.session_key);
    handlemarkAsRead(room.session_key);
    setSelectedRoom(room);
  };

  return (
    <NavbarSidebarLayout>
      <ConversationsSidebarContainer
        rooms={allRooms}
        isLoading={isLoading && currentPage === 1}
        isLoadingMore={isFetching && currentPage > 1}
        hasMoreRooms={hasMoreRooms}
        onLoadMoreRooms={handleLoadMoreRooms}
        onRoomSelect={handleRoomSelect}
      >
        {(selectedRoom) => (
          <ChatAreaChild
            room={selectedRoom}
            wrapper={wrapper as any}
            currentUser={Global.username + Global.companyDomain}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onSendMessage={handleSendMessage}
            onSendFileMessage={handleSendFileMessage}
            onRefreshRooms={syncRoomsFromState}
          />
        )}
      </ConversationsSidebarContainer>
    </NavbarSidebarLayout>
  );
};

export default ChatPage;
